/**
 * Animación de scroll en JS con easing cúbico — no usa scrollIntoView({behavior})
 * porque ese deja la duración a discreción del browser y no se puede tunear.
 *
 * easeInOutCubic: arranca lento → acelera al medio → frena al final.
 *
 * @param {HTMLElement} container - el elemento con overflow-y:auto
 * @param {number} targetTop - scrollTop deseado
 * @param {number} duration - ms (default 550)
 * @returns {() => void} cancel function
 */
export function smoothScrollTo(container, targetTop, duration = 550) {
  if (!container) return () => {};
  const startTop = container.scrollTop;
  const distance = targetTop - startTop;
  if (Math.abs(distance) < 2) return () => {};

  const startTime = performance.now();
  const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  let cancelled = false;
  let rafId = 0;

  const step = (now) => {
    if (cancelled) return;
    const elapsed = now - startTime;
    const t = Math.min(1, elapsed / duration);
    const eased = easeInOutCubic(t);
    container.scrollTop = startTop + distance * eased;
    if (t < 1) rafId = requestAnimationFrame(step);
  };
  rafId = requestAnimationFrame(step);

  return () => {
    cancelled = true;
    if (rafId) cancelAnimationFrame(rafId);
  };
}

/**
 * Encuentra la celda del sticker `n` (vía data-sticker-n) y la centra en
 * el container con scroll suave + un pulso visual breve para que el ojo
 * la encuentre sin esfuerzo.
 *
 * @param {HTMLElement} container - el elemento scrolleable
 * @param {number} n - sticker number
 * @param {object} opts - { duration, pulse, block }
 *   - block: 'center' | 'start' | 'nearest' (default 'center')
 */
export function scrollToSticker(container, n, opts = {}) {
  if (!container) return;
  const { duration = 550, pulse = true, block = 'center' } = opts;
  const elem = container.querySelector(`[data-sticker-n="${n}"]`);
  if (!elem) return;

  const cRect = container.getBoundingClientRect();
  const eRect = elem.getBoundingClientRect();
  const elemTopInContainer = (eRect.top - cRect.top) + container.scrollTop;

  // Si la celda vive dentro de un wrapper marcado con `data-album-card`
  // (header de país + grid), preferimos scrollear de manera que el header
  // del card también quede visible. Caso contrario caemos al comportamiento
  // por número de sticker.
  const card = elem.closest('[data-album-card]');
  let cardTopInContainer = null;
  if (card && container.contains(card)) {
    const kRect = card.getBoundingClientRect();
    cardTopInContainer = (kRect.top - cRect.top) + container.scrollTop;
  }

  let target;
  if (block === 'nearest') {
    if (eRect.top >= cRect.top && eRect.bottom <= cRect.bottom) return;
    target = eRect.top < cRect.top
      ? elemTopInContainer - 16
      : elemTopInContainer - container.clientHeight + elem.clientHeight + 16;
  } else if (cardTopInContainer != null) {
    // Si la celda vive en una card identificada (header del país + grid),
    // siempre llevamos el header del card al top del viewport — eso da una
    // experiencia consistente: tipear ARG, ARG1 o ARG18 te llevan todos al
    // mismo lugar, y el pulso de la celda destacada hace que la encuentres
    // dentro del card.
    target = cardTopInContainer - 12;
  } else if (block === 'start') {
    target = elemTopInContainer - 16;
  } else {
    target = elemTopInContainer - container.clientHeight / 2 + elem.clientHeight / 2;
  }
  // Evitamos scrollear si ya está visible y centrado-ish.
  const distance = Math.abs(target - container.scrollTop);
  if (distance > 4) {
    smoothScrollTo(container, target, duration);
  }

  if (pulse) {
    // El pulso arranca apenas terminada la animación para que el ojo lo
    // encuentre justo cuando la cámara se posa sobre la celda.
    const delay = distance > 4 ? duration : 0;
    setTimeout(() => triggerPulse(elem), delay);
  }
}

let pulseStyleInjected = false;
function ensurePulseStyle() {
  if (pulseStyleInjected) return;
  pulseStyleInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes cm-sticker-pulse {
      0%   { box-shadow: 0 0 0 0 rgba(212, 175, 55, 0.0),    0 0 0 0 rgba(212, 175, 55, 0.0); transform: scale(1); }
      30%  { box-shadow: 0 0 0 6px rgba(212, 175, 55, 0.45), 0 0 14px 6px rgba(212, 175, 55, 0.35); transform: scale(1.08); }
      100% { box-shadow: 0 0 0 0 rgba(212, 175, 55, 0.0),    0 0 0 0 rgba(212, 175, 55, 0.0); transform: scale(1); }
    }
    .cm-sticker-pulse-active {
      animation: cm-sticker-pulse 900ms ease-out;
      z-index: 5 !important;
      position: relative;
    }
  `;
  document.head.appendChild(style);
}

function triggerPulse(elem) {
  ensurePulseStyle();
  elem.classList.remove('cm-sticker-pulse-active');
  // forzar reflow para que la animación pueda re-arrancar
  // eslint-disable-next-line no-unused-expressions
  elem.offsetWidth;
  elem.classList.add('cm-sticker-pulse-active');
  setTimeout(() => elem.classList.remove('cm-sticker-pulse-active'), 950);
}
